import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

export default function useRevenueAnalytics({
  endpoint = "/api/analytics/revenue",
  mode = "cash",
  accrualField = "receivedByDate",
  fromISO,
  toISO,
  grp = "day",
  statusIn = ["Paid", "Partial"],
}) {
  const qs = useMemo(() => {
    const params = new URLSearchParams({
      from: fromISO, to: toISO, grp, mode,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    if (mode === "accrual") params.set("accrualField", accrualField);
    if (statusIn?.length) params.set("statusIn", statusIn.join(","));
    return params.toString();
  }, [fromISO, toISO, grp, mode, accrualField, statusIn?.join(",")]);

  const fetchAnalytics = async () => {
    const res = await fetch(`${endpoint}?${qs}`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };

  const queryKey = useMemo(
    () => ["analytics", endpoint, qs],
    [endpoint, qs]
  );

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey,
    queryFn: fetchAnalytics,
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true,
  });

  return {
    data,
    loading: isLoading || isFetching,
    error: error ? error.message || "Failed to load analytics" : null,
  };
}
